<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { NaverMap, NaverMarker } from "vue3-naver-maps";
import { centerList } from "@/assets/centers";
import type { Center, CenterType } from "@/assets/centers";
import { onMounted } from "vue";

const FILTERS: { name: string; key: CenterType | "all" }[] = [
  { name: "전체보기", key: "all" },
  { name: "Basic", key: "Basic" },
  { name: "Plus", key: "Plus" },
  { name: "Fit", key: "Fit" },
];
const mapIsLoaded = ref<boolean>(false);

const navermap = ref<naver.maps.Map | null>(null);
const mapOptions = {
  zoom: 12,
  logoControl: false,
  mapDataControl: false,
  scaleControl: false,
};
const list = ref(centerList);
const filterType = ref<CenterType | "all">("all");
const selected = ref(centerList[0].name);
const selectedItem = computed(() => {
  return list.value.find((center) => center.name === selected.value);
});

const filterList = computed(() => {
  if (!mapIsLoaded.value) return [];

  return centerList.filter((center) => {
    if (filterType.value === "all") return center;
    return center.type === filterType.value;
  });
});
const selectedLogoRef = ref('')

const onLoadMap = (map: naver.maps.Map) => {
  navermap.value = map;

  mapIsLoaded.value = true;
  navermap.value!.panTo(
    new window.naver.maps.LatLng(
      selectedItem.value!.coords[0],
      selectedItem.value!.coords[1]
    ),
    {}
  );
};
const onLoadMarker = (marker: naver.maps.Marker, center: Center) => {
  const ZINDEX_OVER = 100;
  const ZINDEX_SELECTED = 200;
  const ZINDEX_DEFAULT = 1;

  if (center.name === selected.value) {
    marker.setZIndex(ZINDEX_SELECTED);
  }
  marker.addListener("mouseover", () => {
    if (center.name === selected.value) {
      marker.setZIndex(ZINDEX_SELECTED);
    } else {
      marker.setZIndex(ZINDEX_OVER);
    }
  });
  marker.addListener("mouseout", () => {
    if (center.name === selected.value) {
      marker.setZIndex(ZINDEX_SELECTED);
    } else {
      marker.setZIndex(ZINDEX_DEFAULT);
    }
  });
  marker.addListener("click", () => {
    marker.setZIndex(ZINDEX_SELECTED);
  });

  marker.setIcon({
    content: (marker.getIcon() as any).content,
    anchor: new window.naver.maps.Point(90, 50),
    size: new window.naver.maps.Size(22, 35),
  });
};
const onMarkerClick = async (center: Center) => {
  selected.value = center.name;

  navermap.value!.panTo(
    new window.naver.maps.LatLng(center.coords[0], center.coords[1]),
    {}
  );
};

const logoUrls = import.meta.glob('@/assets/image/logo/**/*', { eager: true });
watch(()=> selectedItem.value, 
  () => {
    if(selectedItem.value){
      const endUrl = `${selectedItem.value!.logo}.${pngLogoCompanys.includes(selectedItem.value!.logo) ? 'png' : 'svg'}`;
      const matchedImage = Object.keys(logoUrls).find((path) => path.endsWith(endUrl));
    
      selectedLogoRef.value = matchedImage ? (logoUrls[matchedImage] as { default: string }).default : '';
    }
})

onMounted(()=> {
  //marker default "genstarmate"
  selectedLogoRef.value = Object.values(logoUrls).map((mod :any) => mod.default)[6];
});

const onClickChangeType = (type: CenterType | "all") => {
  filterType.value = type;
  selected.value = filterList.value[0].name;

  navermap.value!.panTo(
    new window.naver.maps.LatLng(
      selectedItem.value!.coords[0],
      selectedItem.value!.coords[1]
    ),
    {}
  );
};
const pngLogoCompanys = [
  "weverse",
  "alexmuller",
  "blt",
  "inno.n",
  "visionport",
  "42dot",
  "cummins",
  "genuonesciences",
  "goldenplanet",
  "milimeal",
  "pivotpoint",
  "qraft",
  "saintlaurent",
  "shoplive",
  "wtcseoul",
  "shinhansec",
  "hanlim",
];
</script>

<template>
  <div>
    <div class="filter">
      <div
        v-for="filter in FILTERS"
        class="filter__item"
        :class="{ 'filter__item--active': filter.key == filterType }"
        :key="filter.key"
        @click="onClickChangeType(filter.key)"
      >
        <div
          class="filter__bullet"
          :class="{ 'filter__bullet--active': filter.key == filterType }"
        ></div>
        <div>{{ filter.name }}</div>
      </div>
    </div>

    <div class="wrap">
      <div class="map">
        <naver-map
          :map-options="mapOptions"
          class="rounded-[30px] desktop:rounded-r-none w-full h-[440px]"
          @onLoad="onLoadMap"
        >
          <naver-marker
            v-for="center in filterList"
            :key="center.name + filterType"
            :latitude="center.coords[0]"
            :longitude="center.coords[1]"
            @click="onMarkerClick(center)"
            @onLoad="onLoadMarker($event, center)"
          >
            <div
              class="caption"
              :class="{ 'caption--active': center.name == selected }"
            >
              {{ center.name }}
            </div>
            <img
              v-if="center.name === selected"
              src="@/assets/image/marker/select.svg"
              alt="marker"
              class="mx-auto"
            />
            <img
              v-else
              src="@/assets/image/marker/none.svg"
              alt="marker"
              class="mx-auto"
            />
          </naver-marker>
        </naver-map>
      </div>
      <div class="content">
        <div class="min-h-[40px]">
          <img
            v-if="selectedItem!.logo"
            :src="selectedLogoRef"
            :alt="selectedItem!.logo"
            class="content__image"
          />
        </div>

        <div class="content__title">{{selectedItem!.name}}</div>

        <div class="content__address">
          <div>{{selectedItem!.address}}</div>
          <div>{{selectedItem!.detailAddress}}</div>
        </div>

        <div class="content__time">
          <div class="content__time--title">운영시간</div>
          <div>
            <div>
              평일 {{selectedItem!.time.weekday.startTime}} -
              {{selectedItem!.time.weekday.endTime}}
            </div>
            <div v-if="selectedItem!.time.saturday">
              토요일 {{selectedItem!.time.saturday.startTime}} -
              {{selectedItem!.time.saturday.endTime}}
            </div>
            <div v-if="selectedItem!.time.lunch">
              점심 {{selectedItem!.time.lunch.startTime}} -
              {{selectedItem!.time.lunch.endTime}}
            </div>
          </div>
        </div>

        <div class="content__call">
          <div class="content__call--title">대표번호</div>
          <div>{{selectedItem!.call}}</div>
        </div>

        <div class="content__service">
          <div class="flex items-center">
            <div class="content__service--title">서비스</div>
            <div
              class="ml-1 text-xs border border-black3 px-2 rounded text-black6"
            >
              {{selectedItem!.type}}
            </div>
          </div>

          <div class="text-black8 text-sm">
            <span
              v-for="(service,index) in selectedItem!.service"
              :key="service"
            >
              {{ service
              }}<span v-show="selectedItem!.service.length-1 != index">, </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.filter {
  @apply pb-2 desktop:pb-6;
  @apply flex gap-4 desktop:gap-6;

  &__bullet {
    @apply w-2 h-2 rounded-full bg-transparent mx-auto mb-3;

    &--active {
      @apply bg-primary transition-all;
    }
  }
  &__item {
    @apply cursor-pointer font-semibold text-black5;
    @apply desktop:text-2xl;

    &--active {
      @apply text-primary font-bold;
    }
  }
}
.wrap {
  @apply flex flex-col gap-6 h-fit;
  @apply desktop:grid desktop:grid-cols-[2fr_1fr] desktop:gap-0;
}
.map {
  @apply border border-black2 rounded-[30px] h-fit;
  @apply desktop:rounded-r-none desktop:h-auto;
}
.caption {
  @apply text-sm text-black7 font-semibold whitespace-nowrap w-fit relative drop-shadow border border-black4;
  @apply rounded px-2 py-1 mb-1 bg-black2 border border-black3;

  &--active {
    @apply border-primary bg-white rounded-3xl bg-opacity-100;
  }
}

.content {
  @apply border border-black2 rounded-[30px] p-5;
  @apply flex flex-col gap-4;
  @apply desktop:rounded-l-none;

  &__image {
    @apply w-[102px] h-[40px] mx-auto;
  }
  &__title {
    @apply mx-auto text-lg font-bold;
  }
  &__address {
    @apply text-sm text-black6;
  }
  &__time,
  &__call,
  &__service {
    @apply text-black8 flex flex-col gap-2;

    &--title {
      @apply text-black9 font-bold text-sm;
    }
  }
}
</style>
